import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Truck, Package, Users, MapPin, Clock, CheckCircle,
  AlertCircle, LogOut, Upload, Plus, ChevronRight,
  BarChart3, Calendar, FileSpreadsheet, Settings
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    in_progress: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    failed: "bg-red-100 text-red-800 border-red-200"
  };
  
  const labels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    failed: "Failed"
  };
  
  return (
    <Badge variant="outline" className={`${styles[status]} font-medium`}>
      {labels[status]}
    </Badge>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        api.get("/stats"),
        api.get("/jobs")
      ]);
      setStats(statsRes.data);
      setRecentJobs(jobsRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#003366] text-white hidden lg:block">
        <div className="p-6">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-12 mb-6" />
          <p className="text-sm text-blue-200">Admin Dashboard</p>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link
            to="/admin"
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white"
            data-testid="nav-dashboard"
          >
            <BarChart3 className="h-5 w-5" />
            Dashboard
          </Link>
          <Link
            to="/admin/jobs"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100"
            data-testid="nav-jobs"
          >
            <Package className="h-5 w-5" />
            Delivery Jobs
          </Link>
          <Link
            to="/admin/customers"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100"
            data-testid="nav-customers"
          >
            <Users className="h-5 w-5" />
            Customers
          </Link>
          <Link
            to="/admin/drivers"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100"
            data-testid="nav-drivers"
          >
            <Truck className="h-5 w-5" />
            Drivers
          </Link>
          <Link
            to="/admin/upload"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100"
            data-testid="nav-upload"
          >
            <Upload className="h-5 w-5" />
            Import Data
          </Link>
          <Link
            to="/admin/settings"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100"
            data-testid="nav-settings"
          >
            <Settings className="h-5 w-5" />
            Settings
          </Link>
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-sm">{user?.full_name}</p>
              <p className="text-xs text-blue-200">Administrator</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full border-white/20 text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass-header sticky top-0 z-40 border-b border-slate-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-10" />
          <Button variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
              <p className="text-slate-600">Welcome back, {user?.full_name}</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/upload")}
                data-testid="import-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button
                onClick={() => navigate("/admin/jobs")}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="new-job-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Job
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#003366] text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-200 text-sm">Total Jobs</p>
                    <p className="text-3xl font-bold tabular-nums mt-1">{stats?.total_jobs || 0}</p>
                  </div>
                  <Package className="h-10 w-10 text-blue-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-amber-500 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm">Pending</p>
                    <p className="text-3xl font-bold tabular-nums mt-1">{stats?.pending_jobs || 0}</p>
                  </div>
                  <AlertCircle className="h-10 w-10 text-amber-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-emerald-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Completed</p>
                    <p className="text-3xl font-bold tabular-nums mt-1">{stats?.completed_jobs || 0}</p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-700 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-300 text-sm">Drivers</p>
                    <p className="text-3xl font-bold tabular-nums mt-1">{stats?.total_drivers || 0}</p>
                  </div>
                  <Truck className="h-10 w-10 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Today's Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#003366]" />
                Today's Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-4xl font-bold text-[#003366] tabular-nums">{stats?.todays_jobs || 0}</p>
                  <p className="text-slate-600">Scheduled Deliveries</p>
                </div>
                <div className="h-16 w-px bg-slate-200"></div>
                <div>
                  <p className="text-4xl font-bold text-emerald-600 tabular-nums">{stats?.todays_completed || 0}</p>
                  <p className="text-slate-600">Completed</p>
                </div>
                <div className="h-16 w-px bg-slate-200"></div>
                <div>
                  <p className="text-4xl font-bold text-amber-500 tabular-nums">
                    {(stats?.todays_jobs || 0) - (stats?.todays_completed || 0)}
                  </p>
                  <p className="text-slate-600">Remaining</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Jobs */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recent Delivery Jobs</CardTitle>
              <Button variant="ghost" onClick={() => navigate("/admin/jobs")} data-testid="view-all-jobs">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentJobs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No delivery jobs yet</p>
                    <Button
                      variant="link"
                      onClick={() => navigate("/admin/upload")}
                      className="text-[#003366]"
                    >
                      Import your first batch
                    </Button>
                  </div>
                ) : (
                  recentJobs.map((job) => (
                    <div
                      key={job.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => job.proof_id && navigate(`/admin/docket/${job.proof_id}`)}
                      data-testid={`job-row-${job.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Package className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-mono text-sm text-[#003366] font-medium">{job.job_number}</p>
                          <p className="font-medium text-slate-900">{job.customer_name}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {job.delivery_address}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <StatusBadge status={job.status} />
                        <p className="text-sm text-slate-500 mt-2">
                          {job.assigned_driver_name || "Unassigned"}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Mobile Navigation */}
      <nav className="lg:hidden mobile-nav bg-white border-t border-slate-200 h-16">
        <div className="h-full flex items-center justify-around">
          <Link to="/admin" className="flex flex-col items-center gap-1 text-[#003366]">
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Dashboard</span>
          </Link>
          <Link to="/admin/jobs" className="flex flex-col items-center gap-1 text-slate-400">
            <Package className="h-5 w-5" />
            <span className="text-xs">Jobs</span>
          </Link>
          <Link to="/admin/customers" className="flex flex-col items-center gap-1 text-slate-400">
            <Users className="h-5 w-5" />
            <span className="text-xs">Customers</span>
          </Link>
          <Link to="/admin/drivers" className="flex flex-col items-center gap-1 text-slate-400">
            <Truck className="h-5 w-5" />
            <span className="text-xs">Drivers</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
